import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import isAuthenticated from "../../services/authService";
import resumeService from "../../services/resumeService";

const ResumeContext = createContext({
  resumeOffer: null,
  restoredState: null,
  pushState: () => {},
  consumeForPage: () => null,
  acceptResume: () => {},
  ignoreResume: () => {},
});

const excludedPaths = ["/connexion", "/login", "/confirm-otp", "/first-connexion", "/"];

const detectModule = (path = "") => {
  if (path.startsWith("/production/contrats")) return "CONTRATS";
  if (path.startsWith("/production/attestations")) return "ATTESTATIONS";
  if (path.startsWith("/sinistres")) return "SINISTRES";
  if (path.startsWith("/caisse")) return "CAISSE";
  if (path.startsWith("/comptabilite")) return "COMPTABILITE";
  if (path.startsWith("/parametres")) return "PARAMETRES";
  if (path.startsWith("/utilisateurs") || path.startsWith("/ajouter-utilisateur"))
    return "UTILISATEURS";
  return "GENERIC";
};

const deriveSummary = (path = "", payload = {}) => {
  if (payload?.summary) return payload.summary;
  if (path.startsWith("/production/contrats")) return "Contrat en cours";
  if (path.startsWith("/production/attestations")) return "Attestation en cours";
  if (path.startsWith("/sinistres")) return "Dossier sinistre en cours";
  if (path.startsWith("/caisse")) return "Opération caisse en cours";
  return "Session en cours";
};

const restoreSessionSnapshot = (session = {}) => {
  if (typeof sessionStorage === "undefined" || !session) return;
  try {
    Object.entries(session || {}).forEach(([key, value]) => {
      if (typeof value === "string") {
        sessionStorage.setItem(key, value);
      }
    });
  } catch (e) {
    console.warn("ResumeProvider: unable to restore session storage", e);
  }
};

const ResumeProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [resumeOffer, setResumeOffer] = useState(null);
  const [restoredState, setRestoredState] = useState(null);

  const pendingRef = useRef({});
  const saveTimerRef = useRef(null);
  const savingRef = useRef(false);

  const auth = isAuthenticated();

  useEffect(() => {
    if (!auth) return;
    resumeService.fetchLatest().then((data) => {
      if (data?.path) {
        setResumeOffer(data);
      }
    });
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    // Ignore only the exact excluded routes; was using startsWith("/") which skipped everything
    if (excludedPaths.some((p) => p === location.pathname)) return;
    queueSave({
      path: `${location.pathname}${location.search}`,
      module: detectModule(location.pathname),
      summary: deriveSummary(location.pathname),
    });
  }, [auth, location]);

  const flushSave = useCallback(async () => {
    if (savingRef.current) return;
    if (!auth) return;
    const data = pendingRef.current;
    pendingRef.current = {};
    if (!data?.path) return;
    savingRef.current = true;
    try {
      await resumeService.save({
        path: data.path,
        module: data.module,
        summary: data.summary,
        payload: data.payload || {},
      });
    } catch (e) {
      // logging handled in serviceaé
    } finally {
      savingRef.current = false;
    }
  }, [auth]);

  const queueSave = useCallback(
    (partial = {}) => {
      if (!auth) return;
      pendingRef.current = {
        ...pendingRef.current,
        ...partial,
        payload: {
          ...(pendingRef.current.payload || {}),
          ...(partial.payload || {}),
        },
      };
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        flushSave();
      }, 800);
    },
    [auth, flushSave]
  );

  const pushState = useCallback(
    (options = {}) => {
      const path = options.path || `${location.pathname}${location.search}`;
      const payload = options.payload || {};
      queueSave({
        path,
        module: options.module || detectModule(path),
        summary: deriveSummary(path, options),
        payload,
      });
    },
    [location, queueSave]
  );

  const acceptResume = useCallback(async () => {
    if (!resumeOffer) return;
    restoreSessionSnapshot(resumeOffer?.session);
    setRestoredState(resumeOffer);
    setResumeOffer(null);
    await resumeService.clear();
    const target = resumeOffer?.path || "/";
    if (target !== `${location.pathname}${location.search}`) {
      navigate(target, { replace: true });
    }
  }, [navigate, resumeOffer, location]);

  const ignoreResume = useCallback(async () => {
    setResumeOffer(null);
    await resumeService.clear();
  }, []);

  const consumeForPage = useCallback(
    (pageKey) => {
      if (!restoredState) return null;
      const payload = restoredState?.payload || {};
      if (!pageKey || payload?.page === pageKey || payload?.pageKey === pageKey) {
        return payload;
      }
      return null;
    },
    [restoredState]
  );

  const value = useMemo(
    () => ({
      resumeOffer,
      restoredState,
      pushState,
      consumeForPage,
      acceptResume,
      ignoreResume,
    }),
    [resumeOffer, restoredState, pushState, consumeForPage, acceptResume, ignoreResume]
  );

  return <ResumeContext.Provider value={value}>{children}</ResumeContext.Provider>;
};

const useResume = () => useContext(ResumeContext);

export default ResumeProvider;
export { useResume };
